import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Compass, Tv, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Loading } from "@/components/Common";
import { SearchBox } from "@/components/SearchBox";
import { SocialLinks } from "@/components/Footer";
import { MovieCard } from "@/components/MovieCard";

export function Prose({ content }) {
  return (
    <div className="prose-cine">
      {content.split(/\n+/).map((line, i) => line.startsWith("## ") ? <h2 key={i}>{line.slice(3)}</h2> : <p key={i}>{line}</p>)}
    </div>
  );
}

export function StaticPage({ slugOverride, children }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { setP(null); setErr(false); api.get(`/pages/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [slug]);
  useSeo({ title: p?.title || "Page", description: p?.content?.replace(/## /g, "").slice(0, 155) });
  if (err) return <NotFound />;
  if (!p) return <Loading />;
  return (
    <div className="mx-auto max-w-3xl px-4 py-12" data-testid={`page-${slug}`}>
      <nav className="text-xs text-slate-500"><Link to="/">Home</Link> / {p.title}</nav>
      <h1 className="mt-2 font-display text-6xl">{p.title}</h1>
      {children}
      <div className="mt-6"><Prose content={p.content} /></div>
    </div>
  );
}

export function About() {
  const { settings } = useAuth();
  return (
    <StaticPage slugOverride="about">
        <div className="flex flex-col items-center gap-6 rounded-3xl border border-amber-400/20 bg-gradient-to-br from-amber-400/10 to-purple-900/20 p-6 sm:flex-row">
        <div className="flex flex-col items-center gap-4">
          <img src={settings.portrait_url || settings.logo_url} alt="Shivam — creator of Shivam Cine Core" className="h-40 w-40 rounded-full object-cover ring-2 ring-amber-400" />
          <SocialLinks />
        </div>
        <div><p className="font-display text-4xl text-amber-300">Hi, I'm Shivam.</p><p className="mt-2 text-slate-300">{settings.tagline}</p><Link to="/contact" className="mt-4 inline-block text-sm font-semibold text-amber-400">Get in touch →</Link></div>
      </div>
    </StaticPage>
  );
}

export function Contact() {
  const [f, setF] = useState({ name: "", email: "", kind: "general", message: "" });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/contact", f); setSent(true); } catch (err) { toast.error(errMsg(err)); }
  };
  const inp = "w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm outline-none focus:border-amber-400";
  return (
    <StaticPage slugOverride="contact">
      <div className="mt-6 rounded-2xl border border-white/5 bg-[#12121A] p-6">
        {sent ? <p className="text-emerald-300" data-testid="contact-sent">Thanks! Your message has been received.</p> : (
          <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <input data-testid="contact-name" required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" className={inp} />
              <input data-testid="contact-email" required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" className={inp} />
            </div>
            <select data-testid="contact-kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={inp}>
              <option value="general">General question</option><option value="appeal">Moderation appeal</option><option value="takedown">Copyright / takedown</option><option value="partnership">Partnership</option><option value="deletion">Account deletion</option>
            </select>
            <textarea data-testid="contact-message" required minLength={10} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Message (min 10 characters)" className={`${inp} min-h-[120px]`} />
            <button data-testid="contact-submit" className="rounded-full bg-amber-400 px-6 py-2.5 text-sm font-bold text-black">Send message</button>
          </form>
        )}
      </div>
    </StaticPage>
  );
}

export function WhereToWatchPage() {
  const { settings } = useAuth();
  const [items, setItems] = useState([]);
  const [prov, setProv] = useState("");
  useSeo({ title: "Where to Watch — legal streaming guide", description: "Find verified, legal ways to stream, rent or buy movies and series in India and beyond." });
  useEffect(() => { api.get("/titles", { params: { provider: prov || undefined, avail: prov ? undefined : "subscription", limit: 30 } }).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [prov]);
  return (
    <div className="px-4 py-10 sm:px-6 lg:px-12" data-testid="where-to-watch-page">
      <p className="eyebrow">Legal streaming guide</p>
      <h1 className="font-display text-6xl">Where to Watch</h1>
      <p className="mt-2 max-w-2xl text-slate-400">Search any title to see verified streaming, rental and purchase options in your region. We only list official services — never piracy — and say “Availability not confirmed” when we don't know.</p>
      <div className="mt-6 max-w-2xl"><SearchBox big /></div>
      <div className="mt-8 grid gap-3 sm:grid-cols-3">
        {[[Compass, "Search a title", "Find any movie, series or anime."], [Tv, "Pick your region", "India by default, change any time."], [ShieldCheck, "Visit officially", "Every link shows its last-verified date."]].map(([I, a, b]) => (
          <div key={a} className="rounded-2xl border border-white/5 bg-[#12121A] p-5"><I className="h-6 w-6 text-amber-400" /><p className="mt-2 font-semibold">{a}</p><p className="text-sm text-slate-400">{b}</p></div>
        ))}
      </div>
      <div className="mt-10 flex flex-wrap gap-2">
        <button onClick={() => setProv("")} className={`rounded-full px-3 py-1.5 text-xs ${!prov ? "bg-amber-400 text-black" : "bg-white/5"}`}>All subscriptions</button>
        {(settings.providers || []).map((p) => <button key={p.name} data-testid={`wtw-provider-${p.name}`} onClick={() => setProv(p.name)} className={`rounded-full px-3 py-1.5 text-xs ${prov === p.name ? "bg-amber-400 text-black" : "bg-white/5"}`}>{p.name}</button>)}
      </div>
      <div className="mt-6">
        {items.length ? <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">{items.map((t) => <MovieCard key={t.id} t={t} size="fluid" />)}</div>
          : <p className="rounded-xl border border-dashed border-white/10 p-6 text-sm text-slate-500" data-testid="wtw-empty">No verified links {prov ? `for ${prov}` : ""} yet. Availability is added as we confirm it.</p>}
      </div>
    </div>
  );
}

export default function NotFound() {
  useSeo({ title: "Page not found", noindex: true });
  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 text-center" data-testid="not-found">
      <p className="font-display text-[9rem] leading-none text-amber-400">404</p>
      <p className="font-display text-4xl">This scene was cut.</p>
      <p className="mt-2 max-w-md text-slate-400">The page you're looking for doesn't exist or has moved. Try searching instead.</p>
      <div className="mt-6 w-full max-w-md"><SearchBox /></div>
      <Link to="/" className="mt-6 rounded-full bg-amber-400 px-6 py-3 text-sm font-bold text-black">Back to Home</Link>
    </div>
  );
}
